import { getVideoProjectLanguageTtsInstruction } from "./video-project-languages";

/** Delivery style presets (Gemini Director's Notes — Style). */
export const NARRATOR_TTS_STYLE_PRESETS = [
	"neutral",
	"empathetic",
	"warm",
	"authoritative",
	"professional",
	"energetic",
	"calm",
	"conversational",
] as const;
export type NarratorTtsStylePreset =
	(typeof NARRATOR_TTS_STYLE_PRESETS)[number];

/** Speaking pace presets (Gemini Director's Notes — Pace). */
export const NARRATOR_TTS_PACE_PRESETS = [
	"natural",
	"slow",
	"fast",
	"measured",
] as const;
export type NarratorTtsPacePreset = (typeof NARRATOR_TTS_PACE_PRESETS)[number];

/** Accent / dialect presets (Gemini Director's Notes — Accent). */
export const NARRATOR_TTS_ACCENT_PRESETS = [
	"american_neutral",
	"american_southern",
	"british_rp",
	"british_modern",
	"australian",
	"canadian",
	"irish",
	"french_native",
	"arabic_msa",
	"darija_moroccan",
	"auto",
] as const;
export type NarratorTtsAccentPreset =
	(typeof NARRATOR_TTS_ACCENT_PRESETS)[number];

export const NARRATOR_TTS_STYLE_DEFAULT: NarratorTtsStylePreset = "neutral";
export const NARRATOR_TTS_PACE_DEFAULT: NarratorTtsPacePreset = "natural";
export const NARRATOR_TTS_ACCENT_DEFAULT: NarratorTtsAccentPreset =
	"american_neutral";

export const NARRATOR_TTS_STYLE_BRIEFS: Record<NarratorTtsStylePreset, string> =
	{
		neutral:
			"Neutral, clear narration. Plain delivery without gimmicks or exaggerated acting.",
		empathetic:
			"Empathetic and warm. Gentle, understanding delivery that connects with the listener.",
		warm: "Warm and inviting. Friendly, approachable tone with soft diction.",
		authoritative:
			"Authoritative expert host. Confident, precise, documentary gravitas.",
		professional:
			"Professional broadcast voice. Polished, credible, even delivery.",
		energetic:
			"Energetic and upbeat. Lively pacing with engaged, forward momentum.",
		calm: "Calm and soothing. Quiet authority, smooth transitions, minimal intensity spikes.",
		conversational:
			"Conversational and natural. Like talking to one person, not reading a script.",
	};

export const NARRATOR_TTS_PACE_BRIEFS: Record<NarratorTtsPacePreset, string> = {
	natural: "Natural conversational speed. Steady, unhurried, not rushed.",
	slow: "Speak slowly and clearly. Allow pauses between phrases.",
	fast: "Brisk, energetic delivery. Keep momentum without clipping words.",
	measured:
		"Measured, deliberate pacing. Emphasize clarity over speed.",
};

export const NARRATOR_TTS_ACCENT_BRIEFS: Record<
	NarratorTtsAccentPreset,
	string
> = {
	american_neutral:
		"General American English accent — neutral US pronunciation. Not Southern, not Texas drawl, not New York. Keep the same accent on every line.",
	american_southern:
		"Southern American English accent. Warm regional pronunciation consistent across every line.",
	british_rp:
		"British English accent (Received Pronunciation). Consistent British pronunciation on every line.",
	british_modern:
		"Modern British English accent (contemporary UK). Consistent on every line.",
	australian:
		"Australian English accent. Consistent Australian pronunciation on every line.",
	canadian:
		"Canadian English accent. Consistent Canadian pronunciation on every line.",
	irish: "Irish English accent. Consistent Irish pronunciation on every line.",
	french_native:
		"Native French speaker delivery when speaking French. Natural Parisian/neutral French accent.",
	arabic_msa:
		"Modern Standard Arabic (Fusha) pronunciation. Formal, not colloquial dialect.",
	darija_moroccan:
		"Moroccan Darija accent and rhythm. Informal Moroccan Arabic, not Fusha or Egyptian.",
	auto: "Match the document language with a consistent native accent. Do not shift accent between lines.",
};

export interface NarratorTtsDirectorsNotesInput {
	speakerLabel: string;
	language?: string | null;
	ttsStyle?: string | null;
	ttsPace?: string | null;
	ttsAccent?: string | null;
	/** Optional extra director notes (custom accent detail, etc.). */
	ttsNotes?: string | null;
	/** Line-level mood — keep subtle; must not change accent. */
	emotion?: string | null;
	tone?: string | null;
}

function normalizeTtsPreset<T extends string>(
	value: string | null | undefined,
	allowed: readonly T[],
	fallback: T,
): T {
	const key = value?.trim().toLowerCase();
	if (key && (allowed as readonly string[]).includes(key)) return key as T;
	return fallback;
}

export function normalizeNarratorTtsStyle(
	value: string | null | undefined,
): NarratorTtsStylePreset {
	return normalizeTtsPreset(
		value,
		NARRATOR_TTS_STYLE_PRESETS,
		NARRATOR_TTS_STYLE_DEFAULT,
	);
}

export function normalizeNarratorTtsPace(
	value: string | null | undefined,
): NarratorTtsPacePreset {
	return normalizeTtsPreset(
		value,
		NARRATOR_TTS_PACE_PRESETS,
		NARRATOR_TTS_PACE_DEFAULT,
	);
}

export function normalizeNarratorTtsAccent(
	value: string | null | undefined,
	language?: string | null,
): NarratorTtsAccentPreset {
	const normalized = normalizeTtsPreset(
		value,
		NARRATOR_TTS_ACCENT_PRESETS,
		NARRATOR_TTS_ACCENT_DEFAULT,
	);
	if (normalized !== "auto") return normalized;

	const code = (language ?? "en").trim().toLowerCase();
	if (code === "ary") return "darija_moroccan";
	if (code === "ar") return "arabic_msa";
	if (code.startsWith("fr")) return "french_native";
	return NARRATOR_TTS_ACCENT_DEFAULT;
}

export function resolveTtsAccentBrief(
	accent: NarratorTtsAccentPreset,
	language?: string | null,
): string {
	if (accent === "auto") {
		const resolved = normalizeNarratorTtsAccent("auto", language);
		return NARRATOR_TTS_ACCENT_BRIEFS[resolved];
	}
	return NARRATOR_TTS_ACCENT_BRIEFS[accent];
}

/**
 * Gemini-style Director's Notes block for TTS.
 * Separate from script-generation personality / styleNotes.
 */
export function buildTtsDirectorsNotes(input: NarratorTtsDirectorsNotesInput): string {
	const style = normalizeNarratorTtsStyle(input.ttsStyle);
	const pace = normalizeNarratorTtsPace(input.ttsPace);
	const accent = normalizeNarratorTtsAccent(input.ttsAccent, input.language);

	const parts: string[] = [
		`You are speaking as ${input.speakerLabel.trim() || "the narrator"}.`,
	];

	const languageNote = getVideoProjectLanguageTtsInstruction(input.language);
	if (languageNote) parts.push(languageNote);

	parts.push("### DIRECTOR'S NOTES");
	parts.push(`Style: ${NARRATOR_TTS_STYLE_BRIEFS[style]}`);
	parts.push(`Pace: ${NARRATOR_TTS_PACE_BRIEFS[pace]}`);
	parts.push(`Accent: ${resolveTtsAccentBrief(accent, input.language)}`);

	const extra = input.ttsNotes?.trim();
	if (extra) parts.push(extra);

	const emotion = input.emotion?.trim();
	const tone = input.tone?.trim();
	if (emotion || tone) {
		const mood: string[] = [];
		if (emotion) mood.push(`emotion: ${emotion}`);
		if (tone) mood.push(`tone: ${tone}`);
		parts.push(
			`Line delivery (keep the same voice and accent): ${mood.join(", ")}.`,
		);
	}

	parts.push(
		"Read the transcript exactly as written. Do not add words, stage directions, or change the accent mid-line.",
	);

	return parts.join("\n");
}

/** Default accent preset when creating a profile from project language. */
export function defaultTtsAccentForLanguage(
	language: string | null | undefined,
): NarratorTtsAccentPreset {
	return normalizeNarratorTtsAccent("auto", language);
}

/** Short line used for narrator profile TTS previews. */
export function buildNarratorTtsSampleText(
	narratorName?: string | null,
): string {
	const name = narratorName?.trim();
	if (name) {
		return `Hello, I'm ${name}. This is a preview of how I'll narrate your video projects.`;
	}
	return "Hello. This is a preview of how this narrator will sound in your video projects.";
}
